
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bike,
  MapPin,
  ChevronDown,
  ChevronUp,
  Camera,
  School,
  CheckCircle,
  X,
  Eye,
  AlertCircle,
  Users,
  RefreshCw,
  ChevronLeft,
  Navigation,
} from "lucide-react";
import toast from "react-hot-toast";

import {
  getDeliveryPickUps,
  getPickupsSummary,
  pickUpFromParentHome,
  submitAtSchool,
} from "@/services/deliveryAgent";

import { storage } from "@/lib/storage";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useRoleContext } from "@/hooks/useRoleContext";
import { colors } from "@/theme";
import WebcamCapture, { type CapturedPhoto } from "./WebcamCapture";
import { useScreenPermission } from "@/hooks/useScreenPermission";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface Child {
  Child_Name: string;
  Child_Id: string;
  Class: string;
  School_Name: string;
  Is_Absent: boolean;
  Child_Lunch_Status: string;
}

interface ParentData {
  Username: string;
  address: string;

  // Pickup location coordinates
  latitude?: number | string | null;
  longitude?: number | string | null;

  Date: string;
  Total_Count: number;
  Absent_Count: number;
  Present_Count: number;
  Child: Child[];
  Lunch_Status: string;
  Pickup_Status: string;
}

interface SchoolOption {
  label: string;
  value: string;
  schoolCode: string;
  schoolBranch: string | null;
}

// ─────────────────────────────────────────────────────────────
// Photo Box
// ─────────────────────────────────────────────────────────────

function PhotoBox({
  src,
  label,
  onRetake,
  onEye,
}: {
  src: string;
  label: string;
  onRetake?: () => void;
  onEye: () => void;
}) {
  return (
    <div
      className="relative rounded-xl overflow-hidden border border-gray-200 shadow-sm"
      style={{ height: 110 }}
    >
      <img
        src={src}
        alt={label}
        className="w-full h-full object-cover"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />

      <button
        type="button"
        onClick={onEye}
        className="absolute top-2 right-2 w-8 h-8 rounded-lg bg-black/55 border border-white/20 flex items-center justify-center text-white hover:bg-black/75 transition-colors backdrop-blur-sm"
      >
        <Eye className="w-3.5 h-3.5" />
      </button>

      {onRetake && (
        <button
          type="button"
          onClick={onRetake}
          className="absolute bottom-0 inset-x-0 py-1.5 bg-black/55 text-white text-xs font-semibold text-center flex items-center justify-center gap-1 hover:bg-black/70 transition-colors backdrop-blur-sm"
        >
          <Camera className="w-3 h-3" />
          Retake
        </button>
      )}

      <p className="absolute bottom-1.5 left-2 text-white/80 text-xs font-medium">
        {onRetake ? "" : label}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Photo Placeholder
// ─────────────────────────────────────────────────────────────

function PhotoPlaceholder({
  label,
  error,
  onClick,
}: {
  label: string;
  error?: string;
  onClick: () => void;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        className={`w-full rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 py-6 transition-colors ${
          error
            ? "border-red-400 bg-red-50"
            : "border-gray-300 bg-gray-50 hover:bg-gray-100"
        }`}
      >
        <Camera
          className={`w-7 h-7 ${
            error ? "text-red-400" : "text-gray-400"
          }`}
        />

        <p
          className={`text-sm font-medium ${
            error ? "text-red-500" : "text-gray-500"
          }`}
        >
          {label}
        </p>
      </button>

      {error && (
        <p className="text-red-500 text-xs mt-1">
          {error}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────

export default function DeliveryPickups() {
  const navigate = useNavigate();

  const userDetails = useCurrentUser();
  const userData = storage.getUserData();

  const { isBranchIncharge, isZoneManager } =
    useRoleContext();

  const { canCreate } =
    useScreenPermission("pickups");

  const branchCode =
    userDetails?.branch_code ||
    userData?.branch_code ||
    "";

  const zoneCode =
    userDetails?.zone_code ||
    userData?.zone_code ||
    "";

  // ─────────────────────────────────────────────────────────
  // Data
  // ─────────────────────────────────────────────────────────

  const [items, setItems] = useState<ParentData[]>([]);

  const [summary, setSummary] = useState<{
    Total_Deliveries: number;
    Completed_Deliveries: number;
  } | null>(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedIdx, setExpandedIdx] =
    useState<number | null>(null);

  // ─────────────────────────────────────────────────────────
  // Pickup flow
  // ─────────────────────────────────────────────────────────

  const [pickupCameraOpen, setPickupCameraOpen] =
    useState(false);

  const [selectedParent, setSelectedParent] =
    useState<ParentData | null>(null);

  const [pickupLoadingFor, setPickupLoadingFor] =
    useState<string | null>(null);

  // ─────────────────────────────────────────────────────────
  // School delivery
  // ─────────────────────────────────────────────────────────

  const [schoolModalOpen, setSchoolModalOpen] =
    useState(false);

  const [schoolOptions, setSchoolOptions] =
    useState<SchoolOption[]>([]);

  const [selectedSchool, setSelectedSchool] =
    useState<SchoolOption | null>(null);

  const [schoolPhoto, setSchoolPhoto] =
    useState<CapturedPhoto | null>(null);

  const [schoolCameraOpen, setSchoolCameraOpen] =
    useState(false);

  const [schoolLoading, setSchoolLoading] =
    useState(false);

  const [schoolErrors, setSchoolErrors] =
    useState<{
      school?: string;
      photo?: string;
    }>({});

  const [selectedChildren, setSelectedChildren] =
    useState<string[]>([]);

  // ─────────────────────────────────────────────────────────
  // Image modal
  // ─────────────────────────────────────────────────────────

  const [imageModal, setImageModal] =
    useState<string | null>(null);

  // ─────────────────────────────────────────────────────────
  // Success
  // ─────────────────────────────────────────────────────────

  const [successMsg, setSuccessMsg] =
    useState("");

  // ─────────────────────────────────────────────────────────
  // Fetch data
  // ─────────────────────────────────────────────────────────

  const fetchData = useCallback(
    async (quiet = false) => {
      if (!branchCode) return;

      if (!quiet) {
        setLoading(true);
      }

      try {
        const [pickupsRes, summaryRes] =
          await Promise.all([
            getDeliveryPickUps(
              isBranchIncharge ? branchCode : "",
              isZoneManager ? zoneCode : ""
            ),

            getPickupsSummary(
              isBranchIncharge ? branchCode : "",
              isZoneManager ? zoneCode : ""
            ),
          ]);

        const pickupsData =
          (pickupsRes.data as any) || {};

        const pickups =
          pickupsData?.Pickups ?? [];

        setItems(pickups);

        // ─────────────────────────────────────────────
        // IMPORTANT:
        // Store school latitude/longitude from API
        // ─────────────────────────────────────────────

        const schools =
          pickupsData?.Schools ?? [];

        setSchoolOptions(
  schools.map((s: any) => ({
    label: s.School_Name || s.Branch_Name || "",
    value: s.School_Name || s.Branch_Name || "",
    schoolCode: s.School_Code || s.Code || "",
    schoolBranch: s.School_Branch ?? null,
  }))
);

        setSummary(
          (summaryRes.data as any)?.Summary ??
            null
        );
      } catch (error) {
        console.error(
          "Failed to load pickup data:",
          error
        );

        toast.error(
          "Failed to load pickup data."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [
      branchCode,
      zoneCode,
      isBranchIncharge,
      isZoneManager,
    ]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData(true);
  };

const navigateToPickup = (
  pickupLatitude: number | string | null | undefined,
  pickupLongitude: number | string | null | undefined
) => {
  const destinationLat = Number(pickupLatitude);
  const destinationLng = Number(pickupLongitude);

  // Validate pickup coordinates
  if (
    !Number.isFinite(destinationLat) ||
    !Number.isFinite(destinationLng)
  ) {
    toast.error("Pickup location is not available.");
    return;
  }

  if (
    destinationLat < -90 ||
    destinationLat > 90 ||
    destinationLng < -180 ||
    destinationLng > 180
  ) {
    toast.error("Invalid pickup coordinates.");
    return;
  }

  // Browser/device does not support location
  if (!navigator.geolocation) {
    toast.error("Your device does not support location services.");
    return;
  }

  const toastId = toast.loading(
    "Getting your current location..."
  );

  navigator.geolocation.getCurrentPosition(
    (position) => {
      toast.dismiss(toastId);

      const currentLat = position.coords.latitude;
      const currentLng = position.coords.longitude;

      const accuracy = position.coords.accuracy;

      const googleMapsUrl =
        "https://www.google.com/maps/dir/?api=1" +
        `&origin=${encodeURIComponent(
          `${currentLat},${currentLng}`
        )}` +
        `&destination=${encodeURIComponent(
          `${destinationLat},${destinationLng}`
        )}` +
        "&travelmode=driving";


      // Open Google Maps
      window.open(
        googleMapsUrl,
        "_blank",
        "noopener,noreferrer"
      );
    },
    (error) => {
      toast.dismiss(toastId);

      console.error(
        "Unable to get current location:",
        error
      );

      switch (error.code) {
        case 1:
          toast.error(
            "Location permission denied. Please allow location access."
          );
          break;

        case 2:
          toast.error(
            "Current location is unavailable. Please turn on GPS."
          );
          break;

        case 3:
          toast.error(
            "Location request timed out. Please try again."
          );
          break;

        default:
          toast.error(
            "Unable to get your current location."
          );
      }
    },
    {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 0,
    }
  );
};

  const handlePickupCapture = async (
    photo: CapturedPhoto
  ) => {
    setPickupCameraOpen(false);

    if (
      !selectedChildren ||
      selectedChildren.length === 0
    ) {
      toast.error("Please select child");
      return;
    }

    if (!selectedParent) {
      toast.error("Parent information is missing");
      return;
    }

    try {
      setPickupLoadingFor(
        selectedParent.Username
      );

      const formData = new FormData();

      selectedChildren.forEach((id) => {
        formData.append("Child", id);
      });

      formData.append(
        "Parent",
        selectedParent.Username
      );

      formData.append(
        "Attachment",
        photo.blob,
        `pickup_${Date.now()}.jpg`
      );

      const res =
        await pickUpFromParentHome(
          formData
        );

      if (
        res.status === 200 ||
        res.status === 201
      ) {
        setSuccessMsg("Pickup confirmed");

        setSelectedChildren([]);

        await fetchData(true);
      } else {
        toast.error("Pickup failed");
      }
    } catch (error: any) {
      toast.error(
        error?.message ||
          "Something went wrong"
      );
    } finally {
      setPickupLoadingFor(null);
    }
  };

  // ─────────────────────────────────────────────────────────
  // School delivery
  // ─────────────────────────────────────────────────────────

  const openSchoolModal = async () => {
    setSelectedSchool(null);
    setSchoolPhoto(null);
    setSchoolErrors({});
    setSchoolModalOpen(true);
  };

  const handleSchoolDeliver = async () => {
    const errs: typeof schoolErrors = {};

    if (!selectedSchool) {
      errs.school =
        "Please select a school";
    }

    if (!schoolPhoto) {
      errs.photo =
        "Please capture a delivery photo";
    }

    if (Object.keys(errs).length) {
      setSchoolErrors(errs);
      return;
    }

    try {
      setSchoolLoading(true);

      const formData = new FormData();

      formData.append(
        "Branch_Code",
        branchCode
      );

      formData.append(
        "School_Code",
        selectedSchool!.schoolCode
      );

      formData.append(
        "School_Name",
        selectedSchool!.label
      );

      if (
        selectedSchool!.schoolBranch
      ) {
        formData.append(
          "School_Branch",
          selectedSchool!.schoolBranch
        );
      }

      formData.append(
        "Attachment",
        schoolPhoto!.blob,
        `school_${Date.now()}.jpg`
      );

      const res =
        await submitAtSchool(formData);

      if (
        res.status === 200 ||
        res.status === 201
      ) {
        setSchoolModalOpen(false);

        setSuccessMsg(
          "Lunches delivered at school successfully!"
        );

        await fetchData(true);
      } else {
        toast.error(
          (res.data as any)?.message ||
            "Delivery failed."
        );
      }
    } catch (e: any) {
      toast.error(
        e?.message ||
          "Something went wrong."
      );
    } finally {
      setSchoolLoading(false);
    }
  };

  const allDelivered =
    !!summary &&
    summary.Total_Deliveries > 0 &&
    summary.Total_Deliveries ===
      summary.Completed_Deliveries;

  // ─────────────────────────────────────────────────────────
  // Loading
  // ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <PageHeader
          onBack={() =>
            navigate(
              isBranchIncharge
                ? "/branch-incharge-dashboard"
                : isZoneManager
                ? "/zone-manager-dashboard"
                : "/delivery/dashboard"
            )
          }
          onRefresh={handleRefresh}
          refreshing={refreshing}
        />

        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <div
            className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
            style={{
              borderColor: `${colors.brand.primary} transparent transparent transparent`,
            }}
          />

          <p className="text-gray-500 text-sm">
            Loading today's pickups…
          </p>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────

  return (
    <>
      {/* ─────────────────────────────────────────────── */}
      {/* Pickup Camera */}
      {/* ─────────────────────────────────────────────── */}

      <WebcamCapture
        open={pickupCameraOpen}
        onCapture={handlePickupCapture}
        onCancel={() => {
          setPickupCameraOpen(false);
          setSelectedParent(null);
        }}
        hint={
          selectedParent
            ? `Pickup for ${selectedParent.Username}`
            : "Capture pickup photo"
        }
        initialFacing="environment"
      />

      {/* ─────────────────────────────────────────────── */}
      {/* School Camera */}
      {/* ─────────────────────────────────────────────── */}

      <WebcamCapture
        open={schoolCameraOpen}
        onCapture={(p) => {
          setSchoolPhoto(p);
          setSchoolCameraOpen(false);

          setSchoolErrors((prev) => ({
            ...prev,
            photo: undefined,
          }));
        }}
        onCancel={() =>
          setSchoolCameraOpen(false)
        }
        hint="Capture school delivery photo"
        initialFacing="environment"
      />

      {/* ─────────────────────────────────────────────── */}
      {/* Full Image Modal */}
      {/* ─────────────────────────────────────────────── */}

      <AnimatePresence>
        {imageModal && (
          <motion.div
            key="img-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/92 flex items-center justify-center px-4"
            onClick={() =>
              setImageModal(null)
            }
          >
            <button
              type="button"
              onClick={() =>
                setImageModal(null)
              }
              className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-white hover:bg-white/25 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <motion.img
              initial={{
                scale: 0.88,
                opacity: 0,
              }}
              animate={{
                scale: 1,
                opacity: 1,
              }}
              src={imageModal}
              alt="Photo"
              className="max-w-sm w-full rounded-2xl shadow-2xl"
              onClick={(e) =>
                e.stopPropagation()
              }
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─────────────────────────────────────────────── */}
      {/* School Delivery Modal */}
      {/* ─────────────────────────────────────────────── */}

      <AnimatePresence>
        {schoolModalOpen && (
          <motion.div
            key="school-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/60 flex items-end justify-center"
            onClick={() =>
              setSchoolModalOpen(false)
            }
          >
            <motion.div
              initial={{
                y: 80,
                opacity: 0,
              }}
              animate={{
                y: 0,
                opacity: 1,
              }}
              exit={{
                y: 80,
                opacity: 0,
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
              }}
              className="w-full max-w-lg bg-white rounded-t-3xl px-5 pb-8 pt-4 shadow-2xl"
              onClick={(e) =>
                e.stopPropagation()
              }
            >
              {/* Drag handle */}
              <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mb-5" />

              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `${colors.brand.primary}15`,
                  }}
                >
                  <School
                    className="w-5 h-5"
                    style={{
                      color:
                        colors.brand.primary,
                    }}
                  />
                </div>

                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 text-base">
                    Delivery At School
                  </h3>

                  <p className="text-gray-500 text-xs">
                    Select school & capture delivery photo
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setSchoolModalOpen(false)
                  }
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* School dropdown */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  School{" "}
                  <span className="text-red-500">
                    *
                  </span>
                </label>

                <select
                  value={
                    selectedSchool?.value ??
                    ""
                  }
                  onChange={(e) => {
                    const opt =
                      schoolOptions.find(
                        (o) =>
                          o.value ===
                          e.target.value
                      ) ?? null;

                    setSelectedSchool(opt);

                    setSchoolErrors(
                      (prev) => ({
                        ...prev,
                        school: undefined,
                      })
                    );
                  }}
                  className={`w-full rounded-xl border px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 ${
                    schoolErrors.school
                      ? "border-red-400 bg-red-50"
                      : "border-gray-200"
                  }`}
                >
                  <option value="">
                    Select a school
                  </option>

                  {schoolOptions.map(
                    (opt) => (
                      <option
                        key={`${opt.schoolCode}-${opt.value}`}
                        value={opt.value}
                      >
                        {opt.label}
                      </option>
                    )
                  )}
                </select>

                {schoolErrors.school && (
                  <p className="text-red-500 text-xs mt-1">
                    {schoolErrors.school}
                  </p>
                )}
              </div>

              {/* Delivery photo */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Delivery Photo{" "}
                  <span className="text-red-500">
                    *
                  </span>
                </label>

                {schoolPhoto ? (
                  <PhotoBox
                    src={
                      schoolPhoto.previewUrl
                    }
                    label="Delivery photo"
                    onRetake={() =>
                      setSchoolCameraOpen(
                        true
                      )
                    }
                    onEye={() =>
                      setImageModal(
                        schoolPhoto.previewUrl
                      )
                    }
                  />
                ) : (
                  <PhotoPlaceholder
                    label="Tap to capture delivery photo"
                    error={
                      schoolErrors.photo
                    }
                    onClick={() =>
                      setSchoolCameraOpen(
                        true
                      )
                    }
                  />
                )}
              </div>

              {/* Submit */}
              <button
                type="button"
                onClick={handleSchoolDeliver}
                disabled={schoolLoading}
                className="w-full py-4 rounded-2xl font-bold text-white text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-transform hover:scale-[1.01] active:scale-[0.99]"
                style={{
                  background:
                    colors.brand.gradient,
                }}
              >
                {schoolLoading ? (
                  <>
                    <svg
                      className="w-4 h-4 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />

                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4v0z"
                      />
                    </svg>

                    Submitting…
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Mark as Delivered
                  </>
                )}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─────────────────────────────────────────────── */}
      {/* Main */}
      {/* ─────────────────────────────────────────────── */}

      <div className="min-h-screen bg-gray-50">
        <PageHeader
          onBack={() =>
            navigate(
              isBranchIncharge
                ? "/branch-incharge-dashboard"
                : isZoneManager
                ? "/zone-manager-dashboard"
                : "/delivery/dashboard"
            )
          }
          onRefresh={handleRefresh}
          refreshing={refreshing}
        />

        {/* Success banner */}
        <AnimatePresence>
          {successMsg && (
            <motion.div
              initial={{
                opacity: 0,
                y: -12,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              exit={{
                opacity: 0,
                y: -12,
              }}
              className="mx-4 mt-4 flex items-start gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3"
            >
              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />

              <p className="text-green-700 text-sm font-medium flex-1">
                {successMsg}
              </p>

              <button
                type="button"
                onClick={() =>
                  setSuccessMsg("")
                }
                className="text-green-500 hover:text-green-700"
              >
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="px-4 py-4 space-y-3 pb-28">
          {/* Count */}
          <div className="flex items-center gap-2 px-1">
            <Bike
              className="w-4 h-4"
              style={{
                color:
                  colors.brand.primary,
              }}
            />

            <p className="font-bold text-gray-800 text-base">
              {items.length} Pickup
              {items.length !== 1
                ? "s"
                : ""}{" "}
              Today
            </p>
          </div>

          {/* Empty */}
          {items.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-24 gap-4"
            >
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center"
                style={{
                  background: `${colors.brand.primary}10`,
                }}
              >
                <Bike
                  className="w-9 h-9"
                  style={{
                    color:
                      colors.brand.primary,
                  }}
                />
              </div>

              <p className="font-bold text-gray-800 text-lg">
                No Pickups Today
              </p>

              <p className="text-gray-500 text-sm text-center">
                You have no scheduled pickups
                for today.
              </p>
            </motion.div>
          )}

          {/* Pickup cards */}
          {items.map((item, idx) => {
            const isOpen =
              expandedIdx === idx;
            const initials =
              item.Username
                .split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2)
                .toUpperCase();

            const isDone =
              item.Pickup_Status ===
              "Done";

            const isPickingUp =
              pickupLoadingFor ===
              item.Date +
                item.Username;

            return (
              <motion.div
                key={`${item.Date}-${item.Username}-${idx}`}
                initial={{
                  opacity: 0,
                  y: 10,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  delay: idx * 0.04,
                }}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
              >
                {/* Header */}
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-4 py-4 text-left"
                  onClick={() =>
                    setExpandedIdx(
                      isOpen
                        ? null
                        : idx
                    )
                  }
                >
                  {/* Avatar */}
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 font-extrabold text-sm"
                    style={{
                      background: `${colors.brand.primary}15`,
                      color:
                        colors.brand.secondary,
                    }}
                  >
                    {initials}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm truncate">
                      {item.Username}
                    </p>

                    <div className="flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />

                      <p className="text-gray-500 text-xs truncate">
                        {item.address}
                      </p>
                    </div>
                  </div>

                  {/* Pickup action */}
                  {isDone ? (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-green-50 border border-green-200 flex-shrink-0">
                      <CheckCircle className="w-3.5 h-3.5 text-green-600" />

                      <span className="text-green-700 text-xs font-bold">
                        Picked Up
                      </span>
                    </div>
                  ) : !canCreate ? (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-yellow-50 border border-yellow-200 flex-shrink-0">
                      <AlertCircle className="w-3.5 h-3.5 text-yellow-600" />

                      <span className="text-yellow-700 text-xs font-bold">
                        Pending
                      </span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();

                        const childIds =
                          item.Child
                            .filter(
                              (c) =>
                                !c.Is_Absent
                            )
                            .map(
                              (c) =>
                                c.Child_Id
                            );

                        setSelectedChildren(
                          childIds
                        );

                        setSelectedParent(
                          item
                        );

                        setPickupCameraOpen(
                          true
                        );
                      }}
                      disabled={
                        !!pickupLoadingFor
                      }
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl font-bold text-xs flex-shrink-0 disabled:opacity-60 transition-transform hover:scale-105 active:scale-95"
                      style={{
                        background:
                          colors.brand.gradient,
                        color: "#fff",
                      }}
                    >
                      {isPickingUp ? (
                        <svg
                          className="w-3.5 h-3.5 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />

                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4v0z"
                          />
                        </svg>
                      ) : (
                        <Camera className="w-3.5 h-3.5" />
                      )}

                      Pickup
                    </button>
                  )}

                  {/* Chevron */}
                  {isOpen ? (
                    <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0 ml-1" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-1" />
                  )}
                </button>

                {/* ─────────────────────────────────────── */}
                {/* Accordion */}
                {/* ─────────────────────────────────────── */}

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      key="body"
                      initial={{
                        height: 0,
                        opacity: 0,
                      }}
                      animate={{
                        height: "auto",
                        opacity: 1,
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                      }}
                      transition={{
                        duration: 0.22,
                      }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-gray-100 px-4 py-4">

                        <div className="mb-4">
                          <div className="flex items-start gap-2 mb-2">
                            <MapPin
                              className="w-4 h-4 mt-0.5 flex-shrink-0"
                              style={{
                                color:
                                  colors.brand.primary,
                              }}
                            />

                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-semibold text-gray-500">
                                Pickup Location
                              </p>

                              <p className="text-sm font-bold text-gray-800 break-words">
                                {item.address ||
                                  "Pickup address unavailable"}
                              </p>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();

                              navigateToPickup(
                                item.latitude,
                                item.longitude,
                              );
                            }}
                            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white transition-transform active:scale-[0.98]"
                            style={{
                              background:
                                colors.brand.gradient,
                            }}
                          >
                            <Navigation className="w-4 h-4" />

                            Navigate to Pickup
                          </button>

                          {(!item.latitude ||
                            !item.longitude) && (
                            <p className="text-xs text-red-500 mt-1">
                              Pickup coordinates are not available.
                            </p>
                          )}
                        </div>

                        {/* ───────────────────────────── */}
                        {/* Stats */}
                        {/* ───────────────────────────── */}

                        <div className="grid grid-cols-3 gap-2 mb-4">
                          {[
                            {
                              label: "Total",
                              value:
                                item.Total_Count,
                              color:
                                "text-gray-700",
                              bg: "bg-gray-50",
                            },
                            {
                              label:
                                "Present",
                              value:
                                item.Present_Count,
                              color:
                                "text-green-600",
                              bg: "bg-green-50",
                            },
                            {
                              label:
                                "Absent",
                              value:
                                item.Absent_Count,
                              color:
                                "text-red-500",
                              bg: "bg-red-50",
                            },
                          ].map((s) => (
                            <div
                              key={
                                s.label
                              }
                              className={`${s.bg} rounded-xl py-3 text-center`}
                            >
                              <p
                                className={`text-xl font-extrabold ${s.color}`}
                              >
                                {s.value}
                              </p>

                              <p className="text-gray-500 text-xs mt-0.5">
                                {s.label}
                              </p>
                            </div>
                          ))}
                        </div>

                        {/* ───────────────────────────── */}
                        {/* Children */}
                        {/* ───────────────────────────── */}

                        <div className="space-y-2">
                          {item.Child
                            .filter(
                              (c) =>
                                !c.Is_Absent
                            )
                            .map(
                              (child) => (
                                <div
                                  key={
                                    child.Child_Id
                                  }
                                  className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5"
                                >
                                  <div
                                    className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                                    style={{
                                      background: `${colors.brand.primary}15`,
                                      color:
                                        colors.brand.secondary,
                                    }}
                                  >
                                    {child
                                      .Child_Name?.[0]
                                      ?.toUpperCase()}
                                  </div>

                                  <div className="flex-1 min-w-0">
                                    <p className="font-bold text-gray-900 text-sm truncate">
                                      {
                                        child.Child_Name
                                      }
                                    </p>

                                    <p className="text-gray-500 text-xs">
                                      {child.Class}{" "}
                                      ·{" "}
                                      {
                                        child.School_Name
                                      }
                                    </p>
                                  </div>

                                  <span
                                    className={`px-2.5 py-1 rounded-full text-xs font-bold flex-shrink-0 ${
                                      child.Child_Lunch_Status ===
                                      "Done"
                                        ? "bg-green-100 text-green-700"
                                        : "bg-yellow-100 text-yellow-700"
                                    }`}
                                  >
                                    {child.Child_Lunch_Status ===
                                    "Done"
                                      ? "Delivered"
                                      : "Pending"}
                                  </span>
                                </div>
                              )
                            )}
                        </div>

                        {item.Child.filter(
                          (c) =>
                            !c.Is_Absent
                        ).length === 0 && (
                          <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-3">
                            <Users className="w-4 h-4 text-gray-400" />

                            <p className="text-gray-500 text-sm">
                              All children are absent today.
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* ─────────────────────────────────────────────── */}
        {/* Bottom Action Bar */}
        {/* ─────────────────────────────────────────────── */}

        {items.length > 0 && canCreate && (
          <div className="fixed bottom-0 inset-x-0 z-30 px-4 pb-6 pt-3 bg-gradient-to-t from-gray-50 via-gray-50/95 to-transparent">
            {allDelivered ? (
              <div className="flex items-center justify-center gap-2 bg-green-50 border border-green-200 rounded-2xl py-4 shadow-sm">
                <CheckCircle className="w-5 h-5 text-green-600" />

                <span className="text-green-700 font-bold text-sm">
                  All Delivered Today
                </span>
              </div>
            ) : (
              <button
                type="button"
                onClick={openSchoolModal}
                className="w-full py-4 rounded-2xl font-bold text-white text-sm flex items-center justify-center gap-2 shadow-xl transition-transform hover:scale-[1.01] active:scale-[0.99]"
                style={{
                  background:
                    colors.brand.gradient,
                }}
              >
                <School className="w-5 h-5" />

                Delivery At School
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function PageHeader({
  onBack,
  onRefresh,
  refreshing,
}: {
  onBack: () => void;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <div
      className="relative overflow-hidden rounded-b-[28px] shadow-xl px-5 pt-12 pb-5"
      style={{
        background:
          colors.brand.gradient,
      }}
    >
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/[0.05] pointer-events-none" />

      <div className="flex items-center gap-3 relative z-10">
        <button
          type="button"
          onClick={onBack}
          className="w-9 h-9 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center text-white hover:bg-white/25 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex-1">
          <h1 className="text-white font-extrabold text-xl tracking-tight">
            Today's Pickups
          </h1>

          <p className="text-white/55 text-xs">
            Complete pickups and deliver to school
          </p>
        </div>

        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="w-9 h-9 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center text-white hover:bg-white/25 transition-colors"
        >
          <RefreshCw
            className={`w-4 h-4 ${
              refreshing
                ? "animate-spin"
                : ""
            }`}
          />
        </button>
      </div>
    </div>
  );
}